/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useContext, useMemo } from 'preact/hooks';
import { TabbableModeContext } from './TabbableModeContext';
import { useId } from '../UNSAFE_useId';

/**
 * Hook for components that have tabstops. (required for interactive components 
 * that could be used in ListView, Table, Toolbar, DVT, etc).
 *
 * This contract allows a component to cooperate with container focus management systems:
 * - When NOT in tabbable mode, sets tabIndex to -1 so it is skipped by Tab navigation.
 * - When IS in tabbable mode, sets tabIndex to 0 (and participates in roving tabIndex logic if in a managed collection).
 * - Ensures accessibility by preventing keyboard traps and unexpected focus traps.
 *
 * Most components that might be used inside an interactive collection or toolbar should call this and spread the returned props.
 *
 * @param isDisabled Optional. If true, this element is not focusable.
 * @param isRovable Optional (default: true). If false, opt out of roving logic.
 * @returns
 *   - isTabbable: boolean — true if this element should be part of tab navigation right now.
 *   - tabbableModeProps: { tabIndex: number, ... } — props to spread on your focusable element.
 * In order to properly participate in keyboard navigation patterns (including roving tab index navigation),
 * consumers must spread the returned tabbableModeProps on the target tabbable element.

 *
 * @example
 * const { isTabbable, tabbableModeProps } = useTabbableMode(isDisabled);
 * return <div {...tabbableModeProps} />;
 */
function useTabbableMode(isDisabled?: boolean, isRovable = true) {
  const { isTabbable, isRoving, focusedId } = useContext(TabbableModeContext);
  const id = useId();
  const tabbableModeInfo = useMemo(() => {
    const isActive = isTabbable && !isDisabled;

    const tabbableModeProps = {
      tabIndex: isActive && (isRoving ? id === focusedId : true) ? 0 : -1,
      ...(isActive && isRoving && isRovable && { 'data-oj-roving-tabindex-item': id })
    };
    return {
      isTabbable,
      tabbableModeProps
    };
  }, [isDisabled, isRovable, isTabbable, isRoving, focusedId, id]);
  return tabbableModeInfo;
}

export { useTabbableMode };
